import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for handling swipe gestures
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onSwipeLeft - Callback when swiping left
 * @param {Function} options.onSwipeRight - Callback when swiping right
 * @param {Function} options.onSwipeUp - Callback when swiping up
 * @param {Function} options.onSwipeDown - Callback when swiping down
 * @param {number} options.threshold - Minimum distance in pixels to trigger swipe (default: 50)
 * @param {number} options.velocityThreshold - Minimum velocity to trigger swipe (default: 0.3)
 * @param {boolean} options.enabled - Whether swipe detection is enabled (default: true)
 * @returns {Object} - Ref to attach to element and swipe state
 */
export const useSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
  velocityThreshold = 0.3,
  enabled = true,
} = {}) => {
  const elementRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [swipeDistance, setSwipeDistance] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
    touchEndRef.current = null;
    setIsSwiping(true);
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !touchStartRef.current) return;
    
    const touch = e.touches[0];
    if (touchStartRef.current) {
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      
      // Calculate distance
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      setSwipeDistance(distance);
      
      // Determine direction based on larger movement
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        setSwipeDirection(deltaX > 0 ? 'right' : 'left');
      } else {
        setSwipeDirection(deltaY > 0 ? 'down' : 'up');
      }
    }
  }, [enabled]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || !touchStartRef.current) return;
    
    const touch = e.changedTouches[0];
    touchEndRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };

    const deltaX = touchEndRef.current.x - touchStartRef.current.x;
    const deltaY = touchEndRef.current.y - touchStartRef.current.y;
    const deltaTime = touchEndRef.current.time - touchStartRef.current.time;
    
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const velocity = distance / deltaTime; // pixels per ms
    
    // Check if swipe meets threshold requirements
    if (distance < threshold) {
      setIsSwiping(false);
      setSwipeDirection(null);
      setSwipeDistance(0);
      return;
    }

    // Determine swipe direction and trigger callback
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    
    if (absX > absY && absX >= threshold && velocity >= velocityThreshold) {
      // Horizontal swipe
      if (deltaX > 0) {
        onSwipeRight?.();
        setSwipeDirection('right');
      } else {
        onSwipeLeft?.();
        setSwipeDirection('left');
      }
    } else if (absY > absX && absY >= threshold && velocity >= velocityThreshold) {
      // Vertical swipe
      if (deltaY > 0) {
        onSwipeDown?.();
        setSwipeDirection('down');
      } else {
        onSwipeUp?.();
        setSwipeDirection('up');
      }
    }

    // Reset after a short delay
    setTimeout(() => {
      setIsSwiping(false);
      setSwipeDirection(null);
      setSwipeDistance(0);
      touchStartRef.current = null;
      touchEndRef.current = null;
    }, 100);
  }, [enabled, threshold, velocityThreshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, enabled]);

  return {
    ref: elementRef,
    swipeDirection,
    swipeDistance,
    isSwiping,
  };
};

/**
 * Hook specifically for horizontal swipe (left/right)
 */
export const useSwipeHorizontal = ({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  enabled = true,
} = {}) => {
  return useSwipe({
    onSwipeLeft,
    onSwipeRight,
    threshold,
    enabled,
  });
};

/**
 * Hook specifically for vertical swipe (up/down)
 */
export const useSwipeVertical = ({
  onSwipeUp,
  onSwipeDown,
  threshold = 50,
  enabled = true,
} = {}) => {
  return useSwipe({
    onSwipeUp,
    onSwipeDown,
    threshold,
    enabled,
  });
};

/**
 * Hook for swipe to delete functionality
 */
export const useSwipeToDelete = ({
  onDelete,
  threshold = 100,
  enabled = true,
} = {}) => {
  const elementRef = useRef(null);
  const touchStartRef = useRef(null);
  const [translateX, setTranslateX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
    setIsSwiping(true);
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !touchStartRef.current) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    
    // Only allow swiping left (negative deltaX)
    if (deltaX < 0) {
      setTranslateX(deltaX);
    }
  }, [enabled]);

  const handleTouchEnd = useCallback((e) => {
    if (!enabled || !touchStartRef.current) return;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    
    // If swiped far enough left, trigger delete
    if (Math.abs(deltaX) >= threshold && deltaX < 0) {
      onDelete?.();
      setTranslateX(0);
    } else {
      // Snap back
      setTranslateX(0);
    }
    
    setIsSwiping(false);
    touchStartRef.current = null;
  }, [enabled, threshold, onDelete]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, enabled]);

  return {
    ref: elementRef,
    translateX,
    isSwiping,
  };
};

export default useSwipe;

